import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Check, WifiOff, Clock, ChevronUp, X } from 'lucide-react';
import { useOffline } from '../context/OfflineContext';

const SyncIndicator = () => {
  const {
    isOnline, pendingCount, isSyncing, lastSync, syncNow,
  } = useOffline();
  const [showPanel, setShowPanel] = useState(false);
  const [recentSync, setRecentSync] = useState(false);
  const panelRef = useRef(null);

  // Flash green briefly after a successful sync
  useEffect(() => {
    if (!isSyncing && lastSync) {
      setRecentSync(true);
      const t = setTimeout(() => setRecentSync(false), 3000);
      return () => clearTimeout(t);
    }
  }, [isSyncing, lastSync]);

  // Close panel on outside click
  useEffect(() => {
    if (!showPanel) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showPanel]);

  // Don't render if online, no pending, and no recent sync
  const isIdle = isOnline && pendingCount === 0 && !isSyncing && !recentSync;
  if (isIdle && !showPanel) return null;

  // State colors
  const stateColor = !isOnline
    ? 'from-red-500 to-orange-500'
    : pendingCount > 0
      ? 'from-amber-500 to-orange-500'
      : recentSync
        ? 'from-emerald-500 to-green-500'
        : 'from-emerald-500 to-green-500';

  const dotColor = !isOnline
    ? 'bg-red-500'
    : pendingCount > 0
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  const timeSinceSync = lastSync
    ? formatTimeAgo(lastSync)
    : null;

  return (
    <>
      {/* Floating sync pill */}
      <button
        data-testid="sync-indicator-btn"
        onClick={() => setShowPanel(!showPanel)}
        className={`fixed bottom-[76px] right-4 z-50 flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full
          bg-gradient-to-r ${stateColor} shadow-lg shadow-black/30
          transition-all duration-300 active:scale-95
          ${isSyncing ? 'animate-pulse' : ''}
        `}
      >
        {isSyncing ? (
          <RefreshCw className="w-3.5 h-3.5 text-white animate-spin" />
        ) : !isOnline ? (
          <WifiOff className="w-3.5 h-3.5 text-white" />
        ) : recentSync && pendingCount === 0 ? (
          <Check className="w-3.5 h-3.5 text-white" />
        ) : pendingCount > 0 ? (
          <Clock className="w-3.5 h-3.5 text-white" />
        ) : (
          <Check className="w-3.5 h-3.5 text-white" />
        )}

        <span className="text-white text-xs font-semibold">
          {isSyncing
            ? 'Sync...'
            : !isOnline
              ? 'Hors ligne'
              : pendingCount > 0
                ? `${pendingCount}`
                : ''}
        </span>

        {showPanel ? (
          <ChevronUp className="w-3 h-3 text-white/80" />
        ) : null}
      </button>

      {/* Detail panel */}
      {showPanel && (
        <div
          ref={panelRef}
          data-testid="sync-panel"
          className="fixed bottom-[116px] right-4 z-50 w-72 bg-purple-950/95 backdrop-blur-xl rounded-2xl border border-white/15 shadow-2xl shadow-black/40 overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${dotColor}`} />
              <span className="text-white font-semibold text-sm">
                Synchronisation
              </span>
            </div>
            <button
              data-testid="sync-panel-close"
              onClick={() => setShowPanel(false)}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          </div>

          {/* Status */}
          <div className="px-4 py-3 space-y-3">
            {/* Connection state */}
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-xs">Connexion</span>
              <span className={`text-xs font-medium ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>
                {isOnline ? 'En ligne' : 'Hors ligne'}
              </span>
            </div>

            {/* Pending count */}
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-xs">En attente</span>
              <span className={`text-xs font-medium ${pendingCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {pendingCount > 0 ? `${pendingCount} action${pendingCount > 1 ? 's' : ''}` : 'Aucune'}
              </span>
            </div>

            {/* Last sync */}
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-xs">Dernier sync</span>
              <span className="text-white/80 text-xs">
                {timeSinceSync || 'Jamais'}
              </span>
            </div>

            {/* Progress bar during sync */}
            {isSyncing && (
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full animate-sync-progress" />
              </div>
            )}
          </div>

          {/* Action */}
          {isOnline && (
            <div className="px-4 pb-3">
              <button
                data-testid="sync-now-btn"
                onClick={() => { syncNow(); }}
                disabled={isSyncing}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl
                  bg-gradient-to-r from-pink-600 to-purple-600
                  text-white text-xs font-semibold
                  hover:from-pink-500 hover:to-purple-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all active:scale-[0.98]"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Synchronisation en cours...' : 'Synchroniser maintenant'}
              </button>
            </div>
          )}

          {!isOnline && (
            <div className="px-4 pb-3">
              <div className="text-center py-2 px-3 rounded-xl bg-white/5 border border-white/10">
                <span className="text-white/50 text-xs">
                  La synchronisation reprendra automatiquement lors de la reconnexion
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

function formatTimeAgo(date) {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);

  if (diffSec < 30) return "A l'instant";
  if (diffMin < 1) return `${diffSec}s`;
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffHrs < 24) return `Il y a ${diffHrs}h`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default SyncIndicator;
